import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import Spinner from '@medflex/components/stories/Spinner';
import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Button from '@medflex/components/stories/Button';
import RenderFileIcon from '@medflex/components/stories/RenderFileIconModule';
import prettierBytes from 'prettier-bytes';
import InfoBanner, { KINDS } from '@medflex/components/stories/InfoBanner';
import { XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/solid';

const FileUploadView = ({
  fileData,
  isLoading,
  isSyncSucceeded,
  select,
  matchCount,
  reset,
  handleSubmit,
}) => {
  const { t } = useTranslation('physician', { keyPrefix: 'PVSIntegrationModule.uploadSyncFile' });

  if (isLoading) {
    return (
      <div className="flex flex-row mb-4">
        <Spinner size="2xl" />
      </div>
    );
  }

  if (fileData && isSyncSucceeded === null) {
    return (
      <>
        <div className="flex flex-row w-min p-2 border rounded bg-white mb-4">
          <div className="flex px-4 py-2">
            <RenderFileIcon mime="bdt" />
            <div className="flex flex-col">
              <p className="font-semibold">{fileData.name}</p>

              <p className="text-slate-500 text-sm whitespace-nowrap">
                <span>{prettierBytes(fileData.size)}</span>
                <span className="px-1">/</span>
                <span>{fileData.extension}</span>
              </p>
            </div>
          </div>
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <button
            className="ml-6 self-start text-slate-500"
            type="button"
            onClick={() => {
              reset();
            }}
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
        <Button onClick={handleSubmit}>{t('submit')}</Button>
      </>
    );
  }

  return (
    <>
      {isSyncSucceeded === false && (
        <InfoBanner
          kind={KINDS.ERROR}
          className="flex flex-col border border-red-300 rounded-lg items-center text-center mb-4 p-3"
        >
          <div className="text-lg font-semibold inline-flex items-center gap-1 truncate mb-2">
            <XCircleIcon className="w-6 h-6 text-red-500" />
            <Trans t={t} i18nKey="error.title">
              {{ name: fileData?.name }}
            </Trans>
          </div>
          {t('error.description')}
        </InfoBanner>
      )}
      {isSyncSucceeded && (
        <InfoBanner
          kind={KINDS.SUCCESS}
          className="flex flex-col border border-green-300 rounded-lg items-center text-center mb-4 p-3"
        >
          <div className="text-lg font-semibold inline-flex items-center gap-1 truncate mb-2">
            <CheckCircleIcon className="w-6 h-6 text-green-800" />
            <Trans t={t} i18nKey="success.title">
              {{ name: fileData?.name }}
            </Trans>
          </div>
          <p className="whitespace-pre-line">
            <Trans t={t} i18nKey="success.description">
              {{ matched: matchCount.matched }}
              {{ unmatched: matchCount.unmatched }}
            </Trans>
          </p>
        </InfoBanner>
      )}
      <Button kind="primary:outline" onClick={select}>
        <ArrowUpTrayIcon className="w-5 h-5" />
        <span className="ml-2">{t('uploadButton')}</span>
      </Button>
    </>
  );
};

FileUploadView.propTypes = {
  fileData: PropTypes.object,
  isLoading: PropTypes.bool,
  isSyncSucceeded: PropTypes.bool,
  select: PropTypes.func,
  matchCount: PropTypes.object,
  reset: PropTypes.func,
  handleSubmit: PropTypes.func,
};

export default FileUploadView;
